import React, { useEffect, useState, useCallback } from "react";
import { Plane, RefreshCw, Send, ShieldCheck, Unlock, ChevronDown, CircleCheck, Clock, AlertTriangle, PauseCircle, Lock, Radio, FileText } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { fmtNum, fmtFecha } from "@/lib/format";
import { toast } from "sonner";

const Counter = ({ label, value, color, icon: Icon, testid }) => (
  <div data-testid={testid} className="rounded-xl bg-[#050505] border border-[#1F1F24] p-4 flex flex-col gap-2">
    <div className="flex items-center justify-between">
      <span className="text-[10px] text-[#71717A] uppercase tracking-[0.15em]">{label}</span>
      {Icon && <Icon className="w-4 h-4" style={{ color }} />}
    </div>
    <span className="font-mono-ax text-3xl" style={{ color }}>{fmtNum(value)}</span>
  </div>
);

const AccionesTabla = ({ acciones }) => (
  <div className="overflow-x-auto mt-3">
    <table className="w-full text-sm">
      <thead className="bg-[#050505]">
        <tr className="text-[10px] uppercase tracking-wider text-[#71717A]">
          <th className="text-left px-3 py-2">Tipo</th>
          <th className="text-left px-3 py-2">Detalle</th>
          <th className="text-left px-3 py-2">Cuándo</th>
          <th className="text-left px-3 py-2">Estado</th>
        </tr>
      </thead>
      <tbody>
        {(acciones || []).length ? acciones.map((a, i) => (
          <tr key={i} className="border-t border-[#1F1F24]">
            <td className="px-3 py-2 text-[#A1A1AA] text-xs uppercase tracking-wider">{a.tipo}</td>
            <td className="px-3 py-2 text-[#F5F5F7] text-xs">{a.detalle}</td>
            <td className="px-3 py-2 font-mono-ax text-[10px] text-[#71717A]">{fmtFecha(a.timestamp)}</td>
            <td className="px-3 py-2">
              {a.resuelto
                ? <span className="flex items-center gap-1 text-[#4ECDC4] text-xs"><CircleCheck className="w-3.5 h-3.5" /> Resuelta</span>
                : <span className="flex items-center gap-1 text-[#F39C12] text-xs"><Clock className="w-3.5 h-3.5" /> Pendiente</span>}
            </td>
          </tr>
        )) : (
          <tr><td colSpan={4} className="px-3 py-4 text-center text-[#52525b] text-xs">Sin acciones registradas.</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const Informes = () => {
  const [modo, setModo] = useState(false);
  const [guardian, setGuardian] = useState(null);
  const [resumenes, setResumenes] = useState(null);
  const [expandido, setExpandido] = useState(null);
  const [gen, setGen] = useState(false);
  const [pend, setPend] = useState({ modo: false, guardian: false, resumenes: false });

  const cargar = useCallback(async () => {
    try {
      const [m, g, r] = await Promise.all([
        api.get("/comando/modo-viaje"),
        api.get("/comando/guardian"),
        api.get("/comando/guardian/resumenes?limit=30"),
      ]);
      setPend({ modo: !!m.data?.puente_pendiente, guardian: !!g.data?.puente_pendiente, resumenes: !!r.data?.puente_pendiente });
      if (typeof m.data?.activo === "boolean") setModo(m.data.activo);
      setGuardian(g.data);
      setResumenes(r.data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    document.title = "Informes · AXIORA";
    cargar();
    const t = setInterval(cargar, 60000);
    return () => clearInterval(t);
  }, [cargar]);

  const toggleModo = async (val) => {
    setModo(val); // optimista
    try {
      const { data } = await api.post("/comando/modo-viaje", { activo: val });
      if (data?.puente_pendiente) { toast.error("PUENTE PENDIENTE — no se pudo cambiar"); setModo(!val); return; }
      window.dispatchEvent(new Event("axiora-modoviaje"));
      toast.success(val ? "Modo Viaje activado" : "Modo Viaje desactivado");
    } catch (e) { setModo(!val); toast.error("No se pudo cambiar el Modo Viaje"); }
  };

  const generar = async () => {
    setGen(true);
    try {
      const { data } = await api.post("/comando/guardian/enviar-resumen");
      if (data?.puente_pendiente) toast.error("PUENTE PENDIENTE");
      else { toast.success("Informe generado y enviado"); cargar(); }
    } catch (e) { toast.error("No se pudo generar el informe"); }
    finally { setGen(false); }
  };

  const desbloquear = async (uid) => {
    try {
      await api.post(`/comando/usuarios/${uid}/desbloquear`);
      toast.success("Usuario desbloqueado");
      cargar();
    } catch (e) { toast.error("No se pudo desbloquear"); }
  };

  const g = guardian || {};
  const bloqueos = g.bloqueos_retenidos || [];
  const lista = resumenes?.resumenes || [];

  return (
    <div className="p-6 pb-32">
      <header className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="font-serif-ax text-4xl text-[#F5F5F7] tracking-wide flex items-center gap-3">
            <FileText className="w-7 h-7 text-[#D4AF37]" /> Informes Ejecutivos
          </h2>
          <p className="text-xs text-[#71717A] mt-0.5">Resúmenes del Guardián · estado en vivo (24h) · Modo Viaje</p>
        </div>
        <div className="flex items-center gap-3">
          <div data-testid="modo-viaje-switch" className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${modo ? "border-[#2f6fb0] bg-[#1e3a5f]/40 text-[#cfe6ff]" : "border-[#1F1F24] text-[#A1A1AA]"}`}>
            <Plane className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wider">Modo Viaje</span>
            <Switch checked={modo} onCheckedChange={toggleModo} />
          </div>
          <button data-testid="generar-informe-btn" onClick={generar} disabled={gen}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A0A0A] font-semibold text-sm hover:opacity-90 disabled:opacity-50">
            {gen ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Generar informe ahora
          </button>
        </div>
      </header>

      {(pend.modo || pend.guardian || pend.resumenes) && (
        <div data-testid="informes-pendiente" className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-[#F39C12]/10 border border-[#F39C12]/30 text-[#F39C12] text-sm">
          <Radio className="w-4 h-4" /> PUENTE PENDIENTE — algunos datos del Guardián no están disponibles.
        </div>
      )}

      {/* Panel Guardián en vivo */}
      <section className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5 text-[#4ECDC4]" />
          <h3 className="text-sm text-[#F5F5F7] uppercase tracking-wider">Guardián — últimas 24h</h3>
          {g.modo_viaje && <span className="text-[10px] text-[#cfe6ff] bg-[#1e3a5f] border border-[#2f6fb0] rounded-full px-2 py-0.5 flex items-center gap-1"><Plane className="w-3 h-3" /> autónomo</span>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <Counter testid="g-acciones" label="Acciones 24h" value={g.acciones_24h} color="#D4AF37" />
          <Counter testid="g-resueltas" label="Auto-resueltas" value={g.resueltas_24h} color="#4ECDC4" icon={CircleCheck} />
          <Counter testid="g-pendientes" label="Pendientes" value={g.pendientes_24h} color="#F39C12" icon={Clock} />
          <Counter testid="g-standby" label="En standby" value={g.usuarios_standby} color="#A1A1AA" icon={PauseCircle} />
        </div>

        <div className="mb-2 flex items-center gap-2 text-xs text-[#FF6B5B] uppercase tracking-wider">
          <Lock className="w-3.5 h-3.5" /> Esperando tu revisión ({bloqueos.length})
        </div>
        {bloqueos.length ? (
          <div className="space-y-2">
            {bloqueos.map((b) => (
              <div key={b.user_id} data-testid={`bloqueo-${b.user_id}`} className="flex items-center justify-between gap-3 rounded-lg bg-[#FF6B5B]/8 border border-[#FF6B5B]/25 px-4 py-2.5">
                <div className="min-w-0">
                  <p className="text-[#F5F5F7] text-sm font-mono-ax truncate">{b.user_id}</p>
                  <p className="text-[#A1A1AA] text-xs">{b.motivo || "—"}</p>
                  <p className="text-[#52525b] text-[10px] mt-0.5">
                    {fmtFecha(b.desde)} → {fmtFecha(b.hasta)} · renovaciones: {fmtNum(b.renovaciones_guardian)}
                  </p>
                </div>
                <button data-testid={`desbloquear-${b.user_id}`} onClick={() => desbloquear(b.user_id)}
                  className="shrink-0 flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-3 py-2 rounded-md bg-[#4ECDC4]/15 text-[#4ECDC4] border border-[#4ECDC4]/40 hover:bg-[#4ECDC4]/25">
                  <Unlock className="w-3.5 h-3.5" /> Desbloquear
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#52525b] text-sm">No hay bloqueos retenidos esperando revisión.</p>
        )}
      </section>

      {/* Lista de informes (espejo de los emails de 12h) */}
      <section>
        <h3 className="text-sm text-[#F5F5F7] uppercase tracking-wider mb-3">Historial de informes</h3>
        <div className="space-y-3">
          {lista.length ? lista.map((r, i) => {
            const abierto = expandido === i;
            return (
              <div key={i} data-testid={`informe-${i}`} className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] overflow-hidden">
                <button onClick={(e) => {
                    const next = abierto ? null : i;
                    setExpandido(next);
                    if (next !== null) {
                      const card = e.currentTarget.closest(`[data-testid="informe-${i}"]`);
                      setTimeout(() => card?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 60);
                    }
                  }}
                  data-testid={`informe-toggle-${i}`}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.02]">
                  <div className="text-left min-w-0">
                    <p className="text-[#F5F5F7] text-sm truncate">{r.asunto || "Resumen ejecutivo"}</p>
                    <p className="font-mono-ax text-[11px] text-[#71717A] mt-0.5">{fmtFecha(r.sent_at)}{r.modo_viaje ? " · ✈ Modo Viaje" : ""}</p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-xs text-[#4ECDC4]">{fmtNum(r.verdes)}🟢</span>
                    <span className="text-xs text-[#FF6B5B]">{fmtNum(r.rojos)}🔴</span>
                    <ChevronDown className={`w-4 h-4 text-[#71717A] transition-transform ${abierto ? "rotate-180" : ""}`} />
                  </div>
                </button>
                {abierto && (
                  <div className="px-5 pb-5 border-t border-[#1F1F24] pt-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                      <Counter label="Verdes" value={r.verdes} color="#4ECDC4" />
                      <Counter label="Rojos" value={r.rojos} color="#FF6B5B" />
                      <Counter label="Auto-resueltas" value={r.acciones_resueltas} color="#D4AF37" />
                      <Counter label="Bloqueos retenidos" value={r.bloqueos_retenidos} color="#A1A1AA" />
                    </div>
                    {(r.juegos_rojos || []).length > 0 && (
                      <div className="mb-3 rounded-lg bg-[#FF6B5B]/8 border border-[#FF6B5B]/25 p-3">
                        <p className="text-[10px] text-[#FF6B5B] uppercase tracking-wider mb-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Juegos en rojo</p>
                        {r.juegos_rojos.map((j, k) => (
                          <div key={k} className="text-xs text-[#F5F5F7] flex justify-between"><span>{j.nombre}</span><span className="text-[#A1A1AA]">{j.motivo}</span></div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-4 text-[11px] text-[#71717A] mb-1">
                      <span>Pendientes: <span className="text-[#F39C12]">{fmtNum(r.acciones_pendientes)}</span></span>
                      <span>Standby: <span className="text-[#A1A1AA]">{fmtNum(r.standby_pendientes)}</span></span>
                    </div>
                    <AccionesTabla acciones={r.acciones} />
                  </div>
                )}
              </div>
            );
          }) : (
            <div className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] px-5 py-10 text-center text-[#52525b] text-sm">
              Aún no hay informes generados.
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Informes;
